<script setup lang="ts">
import {computed} from 'vue';

type NumberStepperProps = {
  modelValue: number;
  maxCount: number;
};

const props = withDefaults(defineProps<NumberStepperProps>(), {
  modelValue: 0,
});

const emit = defineEmits(['update:modelValue']);

const maxDisabled = computed((): boolean => {
  return !!props.maxCount && props.modelValue >= props.maxCount;
});

function decrease() {
  emit('update:modelValue', props.modelValue - 1);
}

function increase() {
  if (props.maxCount) {
    if (props.modelValue > props.maxCount - 1) {
      return;
    }
  }

  emit('update:modelValue', props.modelValue + 1);
}

function onInput(evt: InputEvent) {
  emit('update:modelValue', Number(evt.target!.value));
}
</script>

<template>
  <div class="number-stepper">
    <button class="button" :disabled="props.modelValue === 0" @click="decrease">-</button>
    <div class="input-value">
      <input type="number" inputmode="numeric" pattern="[0-9]*" :value="props.modelValue" min="1" max="10000" @input="onInput" />
    </div>
    <button class="button" :disabled="maxDisabled" @click="increase">+</button>
  </div>
</template>

<style lang="scss">
@use '@/assets/scss/queries';
.number-stepper {
  align-items: center;
  display: inline-grid;
  gap: 1rem;
  grid-template-columns: auto 1fr auto;
  max-width: 200px;
  touch-action: manipulation;
  width: auto;
  button {
    background-color: white;
    background-position: center;
    background-repeat: no-repeat;
    border: 3px var(--ever-color) solid;
    border-radius: 50%;
    color: var(--ever-color);
    cursor: pointer;
    font-weight: 700;
    height: 30px;
    touch-action: manipulation;
    user-select: none;
    width: 30px;

    &:disabled {
      cursor: not-allowed;
    }

    @include queries.for-size(phone-only) {
      align-items: center;
      display: flex;
      font-size: 2.75rem;
      height: 4.25rem;
      justify-content: center;
      width: 4.25rem;
    }
  }

  .input-value {
    text-align: center;
    input {
      border: 3px black solid;
      font-size: 3.6rem;
      font-weight: 700;
      text-align: center;
      width: 100%;

      &:focus {
        border: 3px black solid;
        outline: none;
      }

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
}
</style>
